import { useState, useEffect } from "react";
import api from "../api";
import SimpleCombobox from "./SimpleCombobox";

const SEVERITY_OPTIONS = [
    { id: 1, name: "Bloqueo", value: 1 },
    { id: 2, name: "Advertencia", value: 2 },
    { id: 3, name: "Informativo", value: 3 },
];

export default function RestrictionForm({
    dialogRef,
    patientId,
    doctorId,
    initialRestriction,
    onSave,
    notify,
    embedded = false,
}) {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [restriction, setRestriction] = useState({
        patient: patientId || null,
        doctor: doctorId || null,
        severity: 2,
        reason: "",
        notes: "",
        expiresAt: "",
        isActive: true,
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        api.doctors.getMulti().then((response) => {
            const doctorsOptions = response.map((doc) => ({
                ...doc,
                name: `${doc.firstName} ${doc.lastName}`,
            }));
            setDoctors(doctorsOptions);
        });
    }, []);

    useEffect(() => {
        if (initialRestriction) {
            setRestriction({
                patient: initialRestriction.patient?.id || patientId,
                doctor: initialRestriction.doctor?.id || doctorId,
                severity: initialRestriction.severity || 2,
                reason: initialRestriction.reason || "",
                notes: initialRestriction.notes || "",
                expiresAt: initialRestriction.expiresAt || "",
                isActive: initialRestriction.isActive ?? true,
            });
        }
    }, [initialRestriction, patientId, doctorId]);

    const handleChange = (field, value) => {
        setRestriction((prev) => ({
            ...prev,
            [field]: value,
        }));
        setErrors((prev) => ({ ...prev, [field]: false }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!restriction.doctor) newErrors.doctor = "Seleccione un médico";
        if (!restriction.reason || restriction.reason.trim().length < 10)
            newErrors.reason = "El motivo debe tener al menos 10 caracteres";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        const payload = {
            patient: restriction.patient,
            doctor: restriction.doctor,
            severity: restriction.severity,
            reason: restriction.reason,
            notes: restriction.notes || null,
            expires_at: restriction.expiresAt || null,
            is_active: restriction.isActive,
        };

        try {
            if (initialRestriction?.id) {
                await api.patientDoctorRestrictions.update(
                    initialRestriction.id,
                    payload
                );
                notify("Restricción actualizada correctamente", "success");
            } else {
                await api.patientDoctorRestrictions.create(payload);
                notify("Restricción guardada correctamente", "success");
            }
            onSave?.();
            if (!embedded) {
                dialogRef.current?.close();
            }
        } catch (error) {
            console.error("Error al guardar restricción:", error);
            if (error?.message && typeof error.message === "object") {
                const messages = Object.values(error.message).flat().join("\n");
                notify(messages, "error");
            } else {
                notify(
                    "Hubo un error al guardar la restricción. Inténtalo nuevamente",
                    "error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 1:
                return "🚫";
            case 2:
                return "⚠️";
            case 3:
                return "ℹ️";
            default:
                return "";
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-black dark:text-white">
            <div className="text-white bg-[#8098F9] dark:bg-[#4f5ea5] p-5">
                <h2 className="text-3xl font-bold">
                    {initialRestriction ? "Editar Restricción" : "Nueva restricción"}
                </h2>
                <small className="text-[#C7D7FE]">
                    Completa los datos para gestionar la restricción entre el paciente y el médico.
                </small>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-6 space-y-4">
            <div>
                <label className="block text-sm font-semibold font-medium text-gray-700 mb-1">
                    Médico  <span className="font-medium text-[#F51515] ml-2">*</span>
                </label>
                <SimpleCombobox
                    name="doctor"
                    options={doctors}
                    value={doctors.find((d) => d.id === restriction.doctor)}
                    handleChange={(name, value) => handleChange("doctor", value?.id)}
                    placeholder="Seleccionar médico"
                    disabled={!!doctorId}
                />
                {errors.doctor && (
                    <p className="text-red-500 text-sm mt-1">{errors.doctor}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Nivel de Restricción  <span className="font-medium text-[#F51515] ml-2">*</span>
                </label>
                <div className="space-y-2">
                    {SEVERITY_OPTIONS.map((option) => (
                        <label
                            key={option.id}
                            className="flex items-center space-x-2 cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="severity"
                                value={option.value}
                                checked={restriction.severity === option.value}
                                onChange={(e) =>
                                    handleChange("severity", parseInt(e.target.value))
                                }
                                className="h-4 w-4 text-blue-600"
                            />
                            <span className="text-lg">{getSeverityIcon(option.value)}</span>
                            <span className="text-sm">{option.name}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Motivo <span className="font-medium text-[#F51515] ml-2">*</span>
                </label>
                <textarea
                    value={restriction.reason}
                    onChange={(e) => handleChange("reason", e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describa el motivo de la restricción (mínimo 10 caracteres)"
                />
                {errors.reason && (
                    <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
                )}
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Notas adicionales
                </label>
                <textarea
                    value={restriction.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Información adicional (opcional)"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de expiración
                </label>
                <input
                    type="date"
                    value={restriction.expiresAt}
                    onChange={(e) => handleChange("expiresAt", e.target.value)}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            <div className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    id="isActive"
                    checked={restriction.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                    className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    Restricción activa
                </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
                {!embedded && (
                    <button
                        type="button"
                        onClick={() => dialogRef.current?.close()}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                )}
                <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    disabled={loading}
                >
                    {loading ? "Guardando..." : "Guardar"}
                </button>
            </div>
        </form>
        </div>
    );
}
